import React from 'react';
import { DisciplineState } from '../../types/discipline';
import { verdictLine, VerdictTone } from '../../utils/disciplineVerdict';

interface Props {
  discipline: DisciplineState;
  /** Name of the rule set in force, e.g. "Keep My Shares" (intent lives in Manage). */
  ruleSet?: string;
  /** 'band' bleeds to the card edges; 'boxed' is a contained panel (Manage modal). */
  variant?: 'band' | 'boxed';
  /** Also say what would change the verdict (Manage modal). */
  showFlip?: boolean;
}

const TONE_ICON: Record<VerdictTone, React.ReactNode> = {
  goal: (<><circle cx="8" cy="8" r="6.2" /><path d="m5.3 8.2 1.9 1.9 3.6-3.8" /></>),
  clock: (<><circle cx="8" cy="8" r="6.2" /><path d="M8 4.6V8l2.3 1.5" /></>),
  risk: (<><path d="M8 1.8 15 14H1L8 1.8Z" /><path d="M8 6.2v3.6M8 11.6v.1" /></>),
  quiet: (<><circle cx="8" cy="8" r="6.2" /><path d="M5 8h6" /></>),
  data: (<><circle cx="8" cy="8" r="6.2" /><path d="M8 4.8v3.8M8 10.8v.1" /></>),
};

/**
 * Verdict band (Oct 2026, card direction B). Replaces the bare-action banner:
 * the rule that fired leads in words, the action follows, and the band's tone
 * comes from the kind of rule (goal / clock / risk), so a profit target reached
 * never looks like an alarm. Every rule the engine checks is listed underneath
 * as "observed / your line", with fired and nearest rules called out in text,
 * not only color. Rule names and display strings are backend-built: rendered
 * verbatim, never reformatted.
 */
const DisciplineStateBanner: React.FC<Props> = ({ discipline, ruleSet, variant = 'band', showFlip = false }) => {
  const v = verdictLine(discipline);
  const rules = discipline.rules ?? [];

  return (
    <div className={`pc-verdict is-${v.tone} ${variant === 'boxed' ? 'is-boxed' : ''}`} role="group" aria-label="Your rules">
      {ruleSet && <span className="pc-verdict-set">{ruleSet} rules</span>}
      <div className="pc-verdict-line">
        <svg viewBox="0 0 16 16" aria-hidden="true">{TONE_ICON[v.tone]}</svg>
        <span>
          <b>{v.headline}</b>
          {v.detail && <> · {v.detail}</>}
          {v.action && <span className="pc-verdict-act"> → {v.action}</span>}
        </span>
      </div>
      {showFlip && discipline.fired_rule && discipline.flip_condition?.summary && (
        <div className="pc-verdict-also">{discipline.flip_condition.summary}</div>
      )}
      {v.alsoFired.length > 0 && (
        <div className="pc-verdict-also">Also fired: {v.alsoFired.join('; ')}</div>
      )}
      {rules.length > 0 && (
        <div className="pc-verdict-rules" role="list">
          {rules.map((r) => (
            <div
              key={r.key}
              role="listitem"
              className={`pc-vrule ${r.fired ? 'is-fired' : ''}`}
              aria-label={`${r.name}: ${r.observed_display}, your line ${r.threshold_display}${r.fired ? ', fired' : r.is_nearest ? ', nearest' : ''}`}
            >
              <span>
                {r.name}
                {r.is_nearest && !r.fired && <em> · nearest</em>}
              </span>
              <b>
                {r.observed_display} / {r.threshold_display}
              </b>
            </div>
          ))}
        </div>
      )}
      {discipline.notes.length > 0 && v.tone !== 'data' && (
        <div className="pc-verdict-notes">
          {discipline.notes.map((n, i) => (
            <span key={i}>{n}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default DisciplineStateBanner;
